// Shares the active session (bypass token or MSAL cache) with newly-opened tabs
// without ever writing it to localStorage. sessionStorage stays tab-scoped and
// is wiped when the browser closes; this just lets a fresh tab ask an already
// signed-in tab to copy its entries over, while at least one tab is still open.

const CHANNEL_NAME = 'bfm-session-sync';

function hasBroadcastChannel() {
    return typeof BroadcastChannel !== 'undefined';
}

function isSessionKey(key) {
    return key === 'finance-ui-bypass-token' || key === 'finance-ui-bypass-user' || key.startsWith('msal.');
}

function hasAnySessionKeys() {
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && isSessionKey(key)) return true;
    }
    return false;
}

function dumpSessionStorage() {
    const entries = {};
    for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && isSessionKey(key)) {
            entries[key] = sessionStorage.getItem(key);
        }
    }
    return entries;
}

// Call once per tab, as early as possible, so this tab can answer requests
// from other tabs even before its own UI has finished loading.
export function initSessionResponder() {
    if (!hasBroadcastChannel()) return;

    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
        if (event.data?.type === 'session-request' && hasAnySessionKeys()) {
            channel.postMessage({ type: 'session-response', entries: dumpSessionStorage() });
        }
    };
}

// Call once at startup, before reading any session state. If this tab has no
// session yet, asks other open tabs for theirs and copies it in. Resolves
// `true` if a session was hydrated, `false` otherwise (including timeout).
export function tryHydrateSessionFromOtherTabs(timeoutMs = 250) {
    return new Promise((resolve) => {
        if (!hasBroadcastChannel() || hasAnySessionKeys()) {
            resolve(false);
            return;
        }

        const channel = new BroadcastChannel(CHANNEL_NAME);
        let settled = false;

        const finish = (result) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            channel.close();
            resolve(result);
        };

        channel.onmessage = (event) => {
            if (event.data?.type === 'session-response' && event.data.entries) {
                Object.entries(event.data.entries).forEach(([key, value]) => {
                    sessionStorage.setItem(key, value);
                });
                finish(true);
            }
        };

        channel.postMessage({ type: 'session-request' });
        const timer = setTimeout(() => finish(false), timeoutMs);
    });
}
